import { action, observable, runInAction, computed, makeObservable } from 'mobx';
import { eventBus } from 'mobx-event-bus2';
import { debounce, isEqual } from 'lodash';

import { PAGE_SIZE } from 'core/constants';
import Logger from 'utils/logger';
import PaginatedDataMixin from 'ui/stores/PaginatedDataMixin';

import { OrderingParams, QueryParams } from 'utils/api/types';

import { DownloadCSVFunctionType, LoadFunctionType, FilterSelected } from './types';
import { Store } from '../DetailsActivities/types';

export function orderingString(order: 'asc' | 'desc', property: string): string {
  return `${order === 'desc' ? '-' : ''}${property}`;
}

export default abstract class TableStore<S extends Store, A, T, R> extends PaginatedDataMixin<A, S> {
  readonly debounceTime = 300; // ms

  @observable items: T[] = [];

  @observable selected: string[] = [];

  @observable filters: FilterSelected[] = [];

  @observable count = 0;

  @observable page = 0;

  @observable rowsPerPage = PAGE_SIZE;

  @observable sortingColumn = 'name';

  @observable sortingOrder: 'asc' | 'desc' = 'asc';

  @observable search = '';

  loadFunction: LoadFunctionType<R>;

  downloadCSVFunction: DownloadCSVFunctionType | null = null;

  debSearch = debounce(this.doLoadData, this.debounceTime);

  constructor(rootStore: S, api: A, loadFunction: LoadFunctionType<R>) {
    super(rootStore, api);

    makeObservable(this);

    this.loadFunction = loadFunction;

    eventBus.register(this);
  }

  @action.bound
  async doSearch(value: string): Promise<void> {
    this.page = 0;
    this.search = value;
    this.debSearch();
  }

  @action.bound
  clearSearch(): void {
    this.search = '';
    this.loadData();
  }

  @action.bound
  async loadData(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoaded = false;
    await this.doLoadData();
  }

  transformResponse(item: R): T {
    return item as unknown as T;
  }

  private async doLoadData(): Promise<void> {
    try {
      const requestedQueryParams = this.queryParams;
      const response = await this.loadFunction(requestedQueryParams)();
      if (isEqual(requestedQueryParams, this.queryParams)) {
        const { results, next, count } = response.data;
        runInAction(() => {
          this.items = results.map(this.transformResponse);
          this.isLoaded = true;
          this.isFailure = false;
          this.hasMore = !!next;
          this.count = count;
        });
      }
    } catch (e) {
      runInAction(() => {
        this.isFailure = true;
      });
      Logger.error('Invalid users list API response', e);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action.bound
  updateRowsPerPage(rowsPerPage: number): void {
    this.rowsPerPage = rowsPerPage;
    this.loadData();
  }

  @action.bound
  updatePage(page: number): void {
    this.page = page;
    this.loadData();
  }

  @action.bound
  updateSortingColumn(sortingColumn: string): void {
    this.sortingColumn = sortingColumn;
    this.loadData();
  }

  @action.bound
  updateSortingOrder(order: 'asc' | 'desc'): void {
    this.sortingOrder = order;
    this.loadData();
  }

  @action.bound
  reloadData(): void {
    this.isLoaded = false;
    this.loadData();
  }

  @action.bound
  updateSelected(selected: string[]): void {
    this.selected = selected;
  }

  @computed get queryParamsFilters(): QueryParams {
    // this.filters looks like [['filter1', 'value1'], ['filter1', 'value2'], ['filter2', 'value3']]
    // First we group by filter key: {filter1: ['value1', 'value2'], filter2: ['value3']}
    const groupByFilterKey = this.filters.reduce(
      (previous: { [k: string]: string[] }, filter) => ({
        ...previous,
        [filter.filter]: [...(previous[filter.filter] || []), filter.value],
      }),
      {}
    );

    // We return then the filters joining the values with a comma as they are used by Django.
    // Result: {filter1: 'value1,value2', filter2: 'value3'}
    return Object.fromEntries(
      Object.entries(groupByFilterKey).map(([filterKey, filterValue]) => [filterKey, filterValue.join(',')])
    );
  }

  getQueryParams(): QueryParams | OrderingParams {
    return {
      ...this.queryParamsFilters,
      query: this.search,
      limit: this.rowsPerPage,
      offset: this.page * this.rowsPerPage,
      ordering: orderingString(this.sortingOrder, this.sortingColumn),
    };
  }

  @computed get queryParams(): QueryParams | OrderingParams {
    return this.getQueryParams();
  }

  downloadCSV(): void {
    if (this.downloadCSVFunction === null) {
      Logger.error('Download CSV Function not set');
      return;
    }
    this.downloadCSVFunction({
      ...this.queryParamsFilters,
      ...(this.selected.length > 0 ? { uuids: this.selected.join(',') } : {}),
      ordering: orderingString(this.sortingOrder, this.sortingColumn),
    })();
  }

  @action.bound
  updateFilters(newFilters: FilterSelected[]): void {
    this.filters = newFilters;
    this.page = 0;
    this.loadData();
  }

  @action
  reset(): void {
    super.reset();

    this.items = [];
    this.count = 0;
    this.selected = [];
    this.filters = [];
  }
}
